import type { ReactNode } from 'react'

import './form.css'

/**
 * 라벨 ↔ 값 한 줄 — 와이어프레임 `field()` 1021행.
 *
 * 🔴 `hint`(설명)를 넣지 않았다. 와이어프레임 원본에는 있지만 §1-1 이
 *    "설명 문장 · 안내문 · 부연 금지" 로 못박는다. 필요한 건 `error` 뿐이고,
 *    그것도 **규칙을 위반했을 때만** 나온다.
 */
export type FieldProps = {
  /** 라벨(한글) */
  label: string
  children: ReactNode
  /** 규칙 위반 문구. 위반했을 때만 넘긴다 */
  error?: string | undefined
  /**
   * 오류 문구에 달 id. 🔴 **입력의 `aria-describedby` 와 묶는 짝이다** — 이것을 안 주면
   * 호출부가 `TextInput.describedBy` 로 가리킨 id 가 **문서에 없는 id** 가 된다(보조기술이
   * 아무것도 못 읽는다). 넘기지 않으면 종전과 똑같이 id 없는 문구다.
   */
  errorId?: string | undefined
  /** 입력 요소의 id. 라벨과 묶는다 */
  htmlFor?: string | undefined
  /**
   * 라벨을 **눈에서만** 숨긴다(`a-sr`). 칸 안 placeholder 가 그 자리를 대신하는 화면용이다
   * (로그인 · eric 2026-09-28 「플레이스 홀더로 인풋에 넣어줘」).
   * 🔴 `<label>` 요소 자체는 남긴다 — placeholder 는 치는 순간 사라져서 보조기술 이름이 될 수 없다.
   */
  labelHidden?: boolean | undefined
  /**
   * 필수 칸 — 라벨 끝에 표시가 붙는다(「추가」 창 넷이 같은 자리 · eric 2026-09-30). 🔴 **서버가 요구하는 칸에만**
   * 단다(없으면 422 로 떨어지는 칸). 이 표시는 미리 말할 뿐이다 — 빈 칸은 주 버튼을 누를 때 검증해 그 칸 아래
   * `error` 로 말한다(`useSubmitValidation` · eric 2026-10-27 「확인 누르면.. 빠진 부분.. 채우라고」). 주 버튼은 살아 있다.
   */
  required?: boolean | undefined
  /** 라벨 바로 옆에 붙일 것(도움말 (?) 단추 등 · 2026-10-28). 안 넘기면 종전 구조 그대로 */
  labelAside?: ReactNode
}

export function Field({
  label,
  children,
  error,
  errorId,
  htmlFor,
  labelHidden,
  required,
  labelAside,
}: FieldProps) {
  return (
    <div className="a-field">
      {/*
        표시는 CSS 가 붙인다(`[data-required]::after`) — 라벨 글자(= 보조기술 이름 · 시험이 찾는 이름)를 바꾸지 않는다.
        빈 필수 칸은 제출 뒤 이 칸 아래 `error` 가 말한다.
      */}
      {labelAside === undefined ? (
        <label
          className={labelHidden === true ? 'a-sr' : 'a-field__label'}
          data-required={required === true ? 'true' : undefined}
          htmlFor={htmlFor}
        >
          {label}
        </label>
      ) : (
        <span className="a-field__labelrow">
          <label
            className={labelHidden === true ? 'a-sr' : 'a-field__label'}
            data-required={required === true ? 'true' : undefined}
            htmlFor={htmlFor}
          >
            {label}
          </label>
          {labelAside}
        </span>
      )}
      <span className="a-field__value">{children}</span>
      {error !== undefined && error !== '' ? (
        <span className="a-field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  )
}
